import { type CharacterSet } from '@tari-labs/emojis-wasm-default';

import { type EmojiCopy } from '@app/client/common/structs/emoji/emoji';
import { CartStatus, type CheckoutResponseV2, type OrderItems } from '@app/client/common/structs/orders/cart';
import config from '@app/config';

import {
    ADD_EMOJI,
    ADD_PRE_EMOJIS,
    COPY_EMOJI,
    type currentIndexInteractionResponse,
    type Emoji,
    type EmojiListStatus,
    GET_CART_ITEMS,
    INIT_SELECT_INDEX,
    NEXT_SEARCH_EMOJI,
    PASTE_EMOJI,
    PREV_SEARCH_EMOJI,
    REMOVE_EMOJI,
    SET_CART_ITEMS,
    SET_CSESSION_ID,
    SET_DELETE_CART_ITEM_REQUEST_IN_PROGRESS,
    SET_EMOJI_LIST,
    SET_INPUT_FOCUSED,
    SET_INPUT_VALUE,
    SET_ORDER_ITEMS,
    SET_RHYTHM_SCORE,
    SET_RHYTHM_SCORE_COPY,
    SET_SEARCH_EMOJI,
    SET_SECRET_KEY,
    SET_WASM_FILTER,
    UPDATE_EMOJIS,
    UPDATE_SEARCH,
    UPDATE_STATUS,
} from '../types/picker';

const MAX_LENGTH = config.maxLengthYatForSale;

export interface EmojiReducerInterface {
    tiles: Array<Emoji>;
    searchResult: Array<Emoji>;
    status: EmojiListStatus;
    currentIndex: number;
    currentIndexAffectsInput: boolean;
    inputFocused: boolean;
    inputValue: string;
    filter?: CharacterSet;
    // TODO define wasm type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    wasm?: any;
    emojiList: Array<Emoji>;
    rhythmScore: number;
    rhythmScoreCopy: EmojiCopy;
    checkoutSession: string;
    orderItems: OrderItems[];
    orderId: string;
    cartStatus?: CartStatus;
    savedSecretKey: string;
    secondsRemaining?: number | null;
    initial: boolean;
    requestInProgress: boolean;
}

interface PickerAction {
    type: string;
    // TODO define action types for reducer 😅
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any;
}

const getEmojiArrayFromString = (str: string) => {
    const split = str.split(/([\uD800-\uDBFF][\uDC00-\uDFFF])/);
    const arr: string[] = [];
    for (let i = 0; i < split.length; i++) {
        const char = split[i];
        if (char !== '') {
            arr.push(char);
        }
    }
    return arr;
};

const compareEmoji = (emojiA: Emoji, emojiB: Emoji, input: string) => {
    if (!emojiA[1].split(' ').includes(input) && !emojiB[1].split(' ').includes(input)) {
        return 0;
    }
    if (emojiB[1] === input) {
        return 1;
    }
    if (emojiA[1].split(' ').includes(input) && emojiB[1].split(' ').includes(input)) {
        const emojiAPart = emojiA[1].split(' ');
        const emojiBPart = emojiB[1].split(' ');
        const aIndex = emojiAPart.reverse().findIndex((str) => str === input);
        const bIndex = emojiBPart.reverse().findIndex((str) => str === input);
        if (bIndex < aIndex) {
            return 1;
        }
        return -1;
    }
    if (!emojiA[1].split(' ').includes(input) && emojiB[1].split(' ').includes(input)) {
        return 1;
    }
    return -1;
};

const initState: EmojiReducerInterface = {
    tiles: [],
    searchResult: [],
    status: 'YAT_UNDEFINED',
    inputFocused: false,
    currentIndex: 0,
    currentIndexAffectsInput: true,
    inputValue: '',
    emojiList: [],
    rhythmScore: 0,
    rhythmScoreCopy: { description: '', features: [] },
    checkoutSession: '',
    orderItems: [],
    orderId: '',
    savedSecretKey: '',
    cartStatus: CartStatus.Draft,
    secondsRemaining: null,
    initial: true,
    requestInProgress: false,
};

export function picker(state: EmojiReducerInterface = initState, action: PickerAction): EmojiReducerInterface {
    switch (action.type) {
        case SET_SEARCH_EMOJI: {
            const { newIndex, newIndexAffectsInput } = action.payload as currentIndexInteractionResponse;
            return {
                ...state,
                currentIndex: newIndex,
                currentIndexAffectsInput: newIndexAffectsInput,
            };
        }
        case NEXT_SEARCH_EMOJI: {
            const { searchResult, currentIndex } = state;
            return {
                ...state,
                currentIndex: currentIndex + 1 > searchResult.length - 1 ? 0 : currentIndex + 1,
                currentIndexAffectsInput: true,
            };
        }
        case PREV_SEARCH_EMOJI: {
            const { searchResult, currentIndex } = state;
            return {
                ...state,
                currentIndex: currentIndex - 1 < 0 ? searchResult.length - 1 : currentIndex - 1,
                currentIndexAffectsInput: true,
            };
        }
        case ADD_EMOJI: {
            const newEmoji = action.payload as Emoji;
            const tiles = [...state.tiles];
            if (tiles.length === MAX_LENGTH) {
                return {
                    ...state,
                };
            }
            return {
                ...state,
                currentIndex: 0,
                tiles: [...tiles, newEmoji],
                inputValue: '',
                searchResult: [],
            };
        }
        case ADD_PRE_EMOJIS: {
            const preEmojis = action.payload as Emoji[];
            return {
                ...state,
                currentIndex: 0,
                tiles: [...preEmojis],
                inputValue: '',
                searchResult: [],
            };
        }
        case SET_INPUT_VALUE: {
            const newValue = action.payload as string;
            return {
                ...state,
                inputValue: newValue,
            };
        }
        case SET_INPUT_FOCUSED: {
            const isFocused = action.payload as boolean;

            return {
                ...state,
                inputFocused: isFocused,
            };
        }
        case UPDATE_SEARCH: {
            const { tiles, filter } = state;
            if (tiles.length >= MAX_LENGTH) return state;

            const newInputValue = action.payload as string;
            let newsearchResult: Array<Emoji> = [];

            if (newInputValue.length > 1) {
                newsearchResult = (filter?.filter(newInputValue, 'All') || []).slice(0, 20);
            } else {
                newsearchResult = [];
            }

            const sortedResult = newsearchResult.sort((emojiA, emojiB) => compareEmoji(emojiA, emojiB, newInputValue));

            return {
                ...state,
                searchResult: sortedResult,
                inputValue: newInputValue,
            };
        }
        case REMOVE_EMOJI: {
            const tiles = [...state.tiles];
            const removalIndex = +action.payload;
            tiles.splice(removalIndex, 1);

            return {
                ...state,
                tiles: [...tiles],
            };
        }
        case COPY_EMOJI: {
            const { tiles } = state;
            navigator.clipboard.writeText(tiles.map((t) => t[0]).join(''));
            return {
                ...state,
            };
        }
        case PASTE_EMOJI: {
            let foundEmojis: Array<Emoji> = [];
            const { emojiList } = state;
            const tiles = [...state.tiles];
            const clipboardContent = action.payload as string;
            const clipEmojis = getEmojiArrayFromString(clipboardContent);
            foundEmojis = clipEmojis
                .filter((e) => emojiList.find((em) => em[0] === e))
                .map((e) => emojiList.find((em) => em[0] === e) || []);
            return {
                ...state,
                tiles: [...tiles, ...foundEmojis].slice(0, MAX_LENGTH),
            };
        }
        case UPDATE_STATUS: {
            const status = action.payload as EmojiListStatus;
            return {
                ...state,
                status,
            };
        }
        case UPDATE_EMOJIS: {
            const emojis = action.payload as Array<Emoji>;
            const tiles = [...state.tiles];

            return {
                ...state,
                tiles: emojis,
                status: tiles.length > 2 && tiles.length < 6 ? 'YAT_DEFINED' : 'YAT_UNDEFINED',
            };
        }
        case INIT_SELECT_INDEX: {
            return {
                ...state,
                currentIndex: 0,
            };
        }
        case SET_WASM_FILTER: {
            return {
                ...state,
                filter: action.payload as CharacterSet,
            };
        }
        case SET_EMOJI_LIST: {
            return {
                ...state,
                emojiList: action.payload as Array<Emoji>,
            };
        }
        case SET_RHYTHM_SCORE: {
            return {
                ...state,
                rhythmScore: action.payload as number,
            };
        }
        case SET_RHYTHM_SCORE_COPY: {
            return {
                ...state,
                rhythmScoreCopy: action.payload as EmojiCopy,
            };
        }
        case SET_CSESSION_ID: {
            return {
                ...state,
                checkoutSession: action.payload as string,
            };
        }
        case SET_ORDER_ITEMS: {
            return {
                ...state,
                orderItems: action.payload as OrderItems[],
            };
        }
        case GET_CART_ITEMS: {
            const { orderItems, orderId, savedSecretKey, secondsRemaining, cartStatus } = action.payload;
            return {
                ...state,
                initial: true,
                orderItems,
                orderId,
                savedSecretKey,
                secondsRemaining,
                cartStatus,
            };
        }
        case SET_CART_ITEMS: {
            const { order_items, seconds_until_expiry, status, payment_method_data, id } =
                action.payload as CheckoutResponseV2;
            return {
                ...state,
                orderItems: order_items,
                savedSecretKey: payment_method_data?.client_secret,
                cartStatus: status as CartStatus,
                secondsRemaining: seconds_until_expiry,
                orderId: id,
            };
        }
        case SET_SECRET_KEY: {
            const savedSecretKey = action.payload as string;
            return {
                ...state,
                savedSecretKey,
            };
        }
        case SET_DELETE_CART_ITEM_REQUEST_IN_PROGRESS: {
            return {
                ...state,
                requestInProgress: action.payload,
            };
        }
        default:
            return state;
    }
}
